/**
 * Yapılandırma listelerinin veri katmanı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * SATIR TİPLERİ `snake_case` — ÇÜNKÜ SUNUCU ÖYLE GÖNDERİYOR.
 *
 * Uçlar ham SQL satırları döndürüyor (`SELECT s.addr_code …`). Burada
 * `camelCase`'e çevirmek "daha temiz" görünürdü ama iki maliyeti var: her
 * alan için elle eşleme yazmak, ve sunucu bir kolon adı değiştirdiğinde
 * derleyicinin susması. Sunucunun sözleşmesini olduğu gibi yazıyoruz;
 * eşleşmeyen bir ad ekranda `undefined` olarak DERHAL görünür.
 *
 * Zod ile doğrulamıyoruz: uçlar şemadan değil SQL'den üretiyor, dolayısıyla
 * buraya yazılacak bir zod şeması sunucunun DOĞRULAMADIĞI ikinci bir gerçek
 * olurdu — [K-020]'nin "tek doğruluk kaynağı" gerekçesinin tersi. Paylaşılan
 * şemalar YAZMA yönünde (form → sunucu) çalışıyor; orada tek kaynak gerçek.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  AlanlarEsit,
  PANO_LISTE_ALANLARI,
  SAHA_DETAY_ALANLARI,
  SAHA_DETAY_PANO_ALANLARI,
  SAHA_DETAY_TANK_ALANLARI,
  SAHA_LISTE_ALANLARI,
  TANK_LISTE_ALANLARI,
} from '@gasco/shared';

import { istek } from '../lib/api.ts';

/**
 * Sunucu listeleri 200 satırda kesiyor (`LIMIT 200`).
 *
 * Bu sayı ARAYÜZDE DE bilinmek zorunda: 200 satır dönen bir liste "hepsi bu"
 * demek değildir ve öyle gösterilirse kullanıcı olmayan bir sahayı yok sanar.
 * Sessiz kırpma bu projede kabul edilmiyor — toplayıcıdaki ring buffer da
 * attığını loglar [K-016].
 */
export const LISTE_TAVANI = 200;

/* ── Satır tipleri ───────────────────────────────────────────────────────── */

export interface SahaSatiri {
  readonly id: number;
  readonly addr_code: string;
  readonly name: string;
  readonly city: string | null;
  readonly district: string | null;
  readonly latitude: number;
  readonly longitude: number;
  readonly customer_id: number | null;
  /** `customers.title` — tabloda `name` kolonu YOK. */
  readonly customer_title: string | null;
  readonly tank_count: number;
  readonly panel_count: number;
  /** Yoklaması AÇIK pano sayısı — "izleniyor mu" bundan türetilir. */
  readonly polling_count: number;
}

export type BaglantiTuru = 'public' | 'vpn';

export interface PanoSatiri {
  readonly id: number;
  readonly serial_number: string;
  readonly site_id: number;
  readonly site_name: string;
  readonly city: string | null;
  readonly connection_mode: BaglantiTuru;
  readonly public_host: string | null;
  readonly tunnel_ip: string | null;
  readonly plc_host: string;
  readonly plc_port: number;
  readonly poll_interval_seconds: number;
  readonly is_polling_enabled: boolean;
  readonly plc_profile_id: number | null;
  readonly plc_brand: string | null;
}

export interface TankSatiri {
  readonly id: number;
  readonly serial_number: string;
  /** `null` → tank DEPODA; henüz bir sahaya kurulmamış. */
  readonly site_id: number | null;
  readonly site_name: string | null;
  readonly tank_number: number | null;
  readonly brand: string | null;
  readonly production_year: number | null;
  readonly orientation: 'vertical' | 'horizontal' | null;
  readonly capacity_m3: number;
  readonly nominal_kg: number | null;
  readonly nominal_sm3: number | null;
}

export interface SahaDetayi {
  readonly site: {
    readonly id: number;
    readonly addr_code: string;
    readonly site_code: string | null;
    readonly name: string;
    readonly city: string | null;
    readonly district: string | null;
    readonly address: string | null;
    readonly customer_id: number | null;
    readonly contact_person: string | null;
    readonly contact_phone: string | null;
    readonly contact_email: string | null;
    readonly latitude: number;
    readonly longitude: number;
  };
  readonly panels: readonly (Omit<PanoSatiri, 'site_id' | 'site_name' | 'city'> & {
    readonly plc_brand: string | null;
  })[];
  readonly tanks: readonly Omit<TankSatiri, 'site_id' | 'site_name' | 'nominal_sm3'>[];
}

/* ── Sözleşme bağı ───────────────────────────────────────────────────────── */

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * ALAN ADLARI DERLEME ZAMANINDA SÖZLEŞMEYE BAĞLI.
 *
 * Yukarıdaki tiplerin anahtarları `@gasco/shared`'daki liste ile BİREBİR
 * olmak zorunda. Bir alanı yanlış yazmak ya da eklemeyi unutmak burada
 * DERLEME HATASI verir — ekranda sessiz bir boşluk olarak değil.
 *
 * Sözleşmenin öbür ucu (gerçek sunucu cevabı) `list-shape-test` ile ölçülüyor:
 * bu satırlar adların BİZİM İÇİMİZDE tutarlı olduğunu garanti eder, sunucunun
 * gerçekten öyle gönderdiğini değil. İkisi birlikte tam kapıyı kapatır.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const _sahaAlanlari: AlanlarEsit<keyof SahaSatiri, (typeof SAHA_LISTE_ALANLARI)[number]> = true;
const _panoAlanlari: AlanlarEsit<keyof PanoSatiri, (typeof PANO_LISTE_ALANLARI)[number]> = true;
const _tankAlanlari: AlanlarEsit<keyof TankSatiri, (typeof TANK_LISTE_ALANLARI)[number]> = true;
const _detayAlanlari: AlanlarEsit<keyof SahaDetayi['site'], (typeof SAHA_DETAY_ALANLARI)[number]> =
  true;
const _detayPanoAlanlari: AlanlarEsit<
  keyof SahaDetayi['panels'][number],
  (typeof SAHA_DETAY_PANO_ALANLARI)[number]
> = true;
const _detayTankAlanlari: AlanlarEsit<
  keyof SahaDetayi['tanks'][number],
  (typeof SAHA_DETAY_TANK_ALANLARI)[number]
> = true;

// Yalnız derleyici için varlar; paketleyici bunları eler.
void _sahaAlanlari;
void _panoAlanlari;
void _tankAlanlari;
void _detayAlanlari;
void _detayPanoAlanlari;
void _detayTankAlanlari;

/* ── Sorgular ────────────────────────────────────────────────────────────── */

/**
 * Sorgu dizesini kurar — BOŞ değerler hiç gönderilmez.
 *
 * `?q=` boş giderse sunucu `%%` araması yapmaz ama sorgu anahtarı değişir ve
 * aynı liste iki kez önbelleğe girerdi.
 */
function sorgu(params: Record<string, string | number | boolean | undefined>): string {
  const parcalar = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parcalar.length === 0 ? '' : `?${parcalar.join('&')}`;
}

export interface ListeFiltresi {
  readonly q?: string | undefined;
  readonly siteId?: number | undefined;
  readonly depoda?: boolean | undefined;
  /** Saha listesi — küresel aramadaki müşteri sonucunun hedefi. */
  readonly musteri?: number | undefined;
  /** Pano listesi — bağlantı türü [K-005 geçişini izlemek için]. */
  readonly mod?: 'public' | 'vpn' | undefined;
  /** Pano listesi — yoklama açık/kapalı. */
  readonly yoklama?: 'acik' | 'kapali' | undefined;
  /** Tank listesi — kapasite sınıfı. */
  readonly kapasite?: number | undefined;
}

export function useSahalar(filtre: ListeFiltresi = {}): UseQueryResult<readonly SahaSatiri[]> {
  const { q, musteri } = filtre;
  return useQuery({
    queryKey: ['sahalar', { q: q ?? null, musteri: musteri ?? null }],
    queryFn: async () => {
      const cevap = await istek<{ sites: SahaSatiri[] }>(`/sites${sorgu({ q, musteri })}`);
      return cevap.sites;
    },
  });
}

export function usePanolar(filtre: ListeFiltresi = {}): UseQueryResult<readonly PanoSatiri[]> {
  const { q, siteId, mod, yoklama } = filtre;
  return useQuery({
    queryKey: [
      'panolar',
      { q: q ?? null, siteId: siteId ?? null, mod: mod ?? null, yoklama: yoklama ?? null },
    ],
    queryFn: async () => {
      const cevap = await istek<{ panels: PanoSatiri[] }>(
        `/panels${sorgu({ q, siteId, mod, yoklama })}`,
      );
      return cevap.panels;
    },
  });
}

export function useTanklar(filtre: ListeFiltresi = {}): UseQueryResult<readonly TankSatiri[]> {
  const { q, siteId, depoda, kapasite } = filtre;
  return useQuery({
    queryKey: [
      'tanklar',
      { q: q ?? null, siteId: siteId ?? null, depoda: depoda ?? false, kapasite: kapasite ?? null },
    ],
    queryFn: async () => {
      // Sunucu `?depoda=1` bekliyor — `true` değil.
      const cevap = await istek<{ tanks: TankSatiri[] }>(
        `/tanks${sorgu({ q, siteId, kapasite, depoda: depoda === true ? '1' : undefined })}`,
      );
      return cevap.tanks;
    },
  });
}

export function useSaha(id: number): UseQueryResult<SahaDetayi> {
  return useQuery({
    queryKey: ['saha', id],
    queryFn: () => istek<SahaDetayi>(`/sites/${id}`),
  });
}

/**
 * Tek pano / tek tank — DÜZENLEME formunun kaynağı.
 *
 * Listeden bulmak yetmez: kullanıcı düzenleme adresini yer imine ekleyebilir
 * ya da sayfayı yenileyebilir; ayrıca liste `LIMIT 200` uyguluyor.
 */
export function usePano(id: number): UseQueryResult<PanoSatiri> {
  return useQuery({
    queryKey: ['pano', id],
    queryFn: async () => (await istek<{ panel: PanoSatiri }>(`/panels/${id}`)).panel,
  });
}

export function useTank(id: number): UseQueryResult<TankSatiri> {
  return useQuery({
    queryKey: ['tank', id],
    queryFn: async () => (await istek<{ tank: TankSatiri }>(`/tanks/${id}`)).tank,
  });
}

/* ── Seçim listeleri ─────────────────────────────────────────────────────── */

/*
 * Bunların işlevi kullanıcıya seçenek sunmak DEĞİL, serbest metin yazmasını
 * ENGELLEMEK. Eski sistemde kapasite serbest sayıydı ve tabloda karşılığı
 * olmayan 20 m³'lük bir tank oluşmuştu (S13): kg hesabı o tank için sessizce
 * çalışmıyordu. Kapasite artık listeden seçilir.
 */

export interface Musteri {
  readonly id: number;
  readonly customer_code: string;
  readonly title: string;
  readonly site_count: number;
}

export interface PlcProfili {
  readonly id: number;
  readonly brand_name: string;
  readonly description: string | null;
  readonly register_count: number;
  readonly verified_count: number;
}

export interface TankKapasitesi {
  readonly capacity_m3: number;
  readonly nominal_kg: number;
  readonly nominal_sm3: number | null;
}

/** Seçim listeleri nadiren değişir — 5 dk taze sayılır, her formda istenmez. */
const SECIM_TAZELIGI = 5 * 60_000;

export function useMusteriler(): UseQueryResult<readonly Musteri[]> {
  return useQuery({
    queryKey: ['musteriler'],
    queryFn: async () => (await istek<{ customers: Musteri[] }>('/customers')).customers,
    staleTime: SECIM_TAZELIGI,
  });
}

export function usePlcProfilleri(): UseQueryResult<readonly PlcProfili[]> {
  return useQuery({
    queryKey: ['plc-profilleri'],
    queryFn: async () => (await istek<{ profiles: PlcProfili[] }>('/plc-profiles')).profiles,
    staleTime: SECIM_TAZELIGI,
  });
}

export function useTankKapasiteleri(): UseQueryResult<readonly TankKapasitesi[]> {
  return useQuery({
    queryKey: ['tank-kapasiteleri'],
    queryFn: async () =>
      (await istek<{ capacities: TankKapasitesi[] }>('/tank-capacities')).capacities,
    staleTime: SECIM_TAZELIGI,
  });
}

/* ── Yazma ───────────────────────────────────────────────────────────────── */

/**
 * Kaydetmeden sonra ETKİLENEN her liste geçersiz kılınır.
 *
 * Tek tek anahtar saymak yerine kök anahtara göre siliniyor: yeni bir saha
 * eklendiğinde saha listesi, saha detayı ve — sahaya bağlı olduğu için —
 * pano/tank listeleri de bayat olabilir. Eksik bırakılan bir anahtar
 * kullanıcıya "kaydettim ama listede yok" dedirtir; bu, formun en sık
 * bildirilen hata sınıfıdır.
 */
export const YAZMA_SONRASI_ANAHTARLAR = [
  ['sahalar'],
  ['saha'],
  ['panolar'],
  ['pano'],
  ['tanklar'],
  ['tank'],
] as const;
